import Dexie from "dexie"
import { clearDbBlocked, notifyDbBlocked } from "./boot-health"
import { markBootRecovery } from "./boot-recovery-log"
import {
  WORKFLOW_TABLE,
  WORKFLOW_TABLE_SCHEMA,
} from "./resources/ai/workflow/db"
import {
  EXECUTION_TABLE,
  EXECUTION_TABLE_SCHEMA,
} from "./resources/ai/execution/db"
import {
  CHAT_SESSION_TABLE,
  CHAT_SESSION_TABLE_SCHEMA,
} from "./resources/ai/chat-session/db"
import {
  CHAT_MESSAGE_TABLE,
  CHAT_MESSAGE_TABLE_SCHEMA,
} from "./resources/ai/chat-message/db"
import {
  CHAT_EXECUTION_TABLE,
  CHAT_EXECUTION_TABLE_SCHEMA,
} from "./resources/ai/chat-execution/db"
import {
  CHAT_MEDIA_TABLE,
  CHAT_MEDIA_TABLE_SCHEMA,
} from "./resources/ai/chat-media/db"
import {
  PROPOSAL_BATCH_TABLE,
  PROPOSAL_BATCH_TABLE_SCHEMA,
} from "./resources/ai/proposal-batch/db"
import {
  EMAIL_TABLE,
  EMAIL_TABLE_SCHEMA,
} from "./resources/communications/email/db"
import {
  SMS_CHAT_TABLE,
  SMS_CHAT_TABLE_SCHEMA,
} from "./resources/communications/sms-chat/db"
import {
  SMS_MESSAGE_TABLE,
  SMS_MESSAGE_TABLE_SCHEMA,
} from "./resources/communications/sms-message/db"
import {
  WHATSAPP_CHAT_TABLE,
  WHATSAPP_CHAT_TABLE_SCHEMA,
} from "./resources/communications/whatsapp-chat/db"
import {
  WHATSAPP_MESSAGE_TABLE,
  WHATSAPP_MESSAGE_TABLE_SCHEMA,
} from "./resources/communications/whatsapp-message/db"
import {
  PRODUCT_TABLE,
  PRODUCT_TABLE_SCHEMA,
} from "./resources/inventory/product/db"
import { UNIT_TABLE, UNIT_TABLE_SCHEMA } from "./resources/inventory/unit/db"
import {
  PRODUCT_UNIT_TABLE,
  PRODUCT_UNIT_TABLE_SCHEMA,
} from "./resources/inventory/product-unit/db"
import {
  LOCATION_TABLE,
  LOCATION_TABLE_SCHEMA,
} from "./resources/inventory/location/db"
import { LOT_TABLE, LOT_TABLE_SCHEMA } from "./resources/inventory/lot/db"
import { STOCK_TABLE, STOCK_TABLE_SCHEMA } from "./resources/inventory/stock/db"
import { PARTNER_TABLE, PARTNER_TABLE_SCHEMA } from "./resources/crm/partner/db"
import {
  PARTNER_ADDRESS_TABLE,
  PARTNER_ADDRESS_TABLE_SCHEMA,
} from "./resources/crm/partner-address/db"
import {
  EMAIL_CONTACT_TABLE,
  EMAIL_CONTACT_TABLE_SCHEMA,
} from "./resources/communications/email-contact/db"
import {
  PARTNER_CONTACT_TABLE,
  PARTNER_CONTACT_TABLE_SCHEMA,
} from "./resources/crm/partner-contact/db"
import {
  CLIENT_GROUP_TABLE,
  CLIENT_GROUP_TABLE_SCHEMA,
} from "./resources/crm/client-group/db"
import {
  PARTNER_ACCOUNT_TABLE,
  PARTNER_ACCOUNT_TABLE_SCHEMA,
} from "./resources/crm/partner-account/db"
import {
  PRE_ORDER_TABLE,
  PRE_ORDER_TABLE_SCHEMA,
} from "./resources/orders/pre-order/db"
import {
  PRE_ORDER_FLOW_TABLE,
  PRE_ORDER_FLOW_TABLE_SCHEMA,
} from "./resources/orders/pre-order-flow/db"
import {
  PRE_ORDER_MESSAGE_TABLE,
  PRE_ORDER_MESSAGE_TABLE_SCHEMA,
} from "./resources/orders/pre-order-message/db"
import {
  PRE_ORDER_SMS_MESSAGE_TABLE,
  PRE_ORDER_SMS_MESSAGE_TABLE_SCHEMA,
} from "./resources/orders/pre-order-sms-message/db"
import {
  PRE_ORDER_EMAIL_TABLE,
  PRE_ORDER_EMAIL_TABLE_SCHEMA,
} from "./resources/orders/pre-order-email/db"
import { PRICE_TABLE, PRICE_TABLE_SCHEMA } from "./resources/pricing/price/db"
import {
  FEEDBACK_TABLE,
  FEEDBACK_TABLE_SCHEMA,
} from "./resources/core/feedback/db"
import {
  FEEDBACK_MESSAGE_TABLE,
  FEEDBACK_MESSAGE_TABLE_SCHEMA,
} from "./resources/core/feedback-message/db"
import {
  PRE_ORDER_SUGGESTION_TABLE,
  PRE_ORDER_SUGGESTION_TABLE_SCHEMA,
} from "./resources/ai/pre-order-suggestion/db"
import {
  PRE_ORDER_FLOW_SUGGESTION_TABLE,
  PRE_ORDER_FLOW_SUGGESTION_TABLE_SCHEMA,
} from "./resources/ai/pre-order-flow-suggestion/db"
import {
  PRODUCT_PRICE_SUGGESTION_TABLE,
  PRODUCT_PRICE_SUGGESTION_TABLE_SCHEMA,
} from "./resources/ai/product-price-suggestion/db"
import {
  PARTNER_PRODUCT_PRICE_SUGGESTION_TABLE,
  PARTNER_PRODUCT_PRICE_SUGGESTION_TABLE_SCHEMA,
} from "./resources/ai/partner-product-price-suggestion/db"
import {
  CLIENT_GROUP_PRODUCT_PRICE_SUGGESTION_TABLE,
  CLIENT_GROUP_PRODUCT_PRICE_SUGGESTION_TABLE_SCHEMA,
} from "./resources/ai/client-group-product-price-suggestion/db"
import {
  FLOW_SUGGESTION_TABLE,
  FLOW_SUGGESTION_TABLE_SCHEMA,
} from "./resources/ai/flow-suggestion/db"

export const DB_NAME = "stockline-offline"
// v28: products / partners now carry `isUserProduct` / `isUserPartner` on the
// sync row (computed via JOIN at read time, source of truth still the
// user_company_product / user_company_partner tables). No new indexed fields,
// so no schema-string change is required — bumping the version tells Dexie to
// treat the next open as an upgrade boundary, which makes the change
// reviewable and gives us a hook if we later index the flag.
// v29: client-groups become a first-class offline-first resource with their
// own Dexie store (`client-groups`); the partner row carries denormalised
// `clientGroupIds: string[]` pointers instead of the embedded
// PartnerClientGroupRef[] from the previous PR-C — frontends join those ids
// against the new store at render time. The schema string for `partners`
// itself is unchanged (`clientGroupIds` is non-indexed), so the bump is
// driven by the new store.
// v30: index `deliveryDate` / `orderDate` on `preOrders` (+ `[companyId+
// deliveryDate]` / `[companyId+orderDate]` compounds). Recap was scanning
// 20k+ rows in JS to pick the ~50 visible for a date — Dexie now serves
// those directly from the index. Existing rows are reindexed in place by
// Dexie on first open after upgrade; no data migration needed.
// v31: add `[preOrderId+productId]` compound to `preOrderFlows`. Powers the
// recap product-view bootstrap — `useDistinctProductIdsForPreOrders` calls
// `uniqueKeys()` over the compound to enumerate the distinct product IDs
// for the active date scope without materializing flow rows (~125 ms of
// indexed range scans vs ~1.5 s of full JSON parse on a 4k-flow day).
// v32: add `[preOrderId+productId+updatedAt]` and
// `[preOrderId+productId+createdAt]` compounds on `preOrderFlows`. Power
// the recap product-view flow-time sort comparators (`latest-updated` /
// `oldest-updated` / `latest-created` / `oldest-created`) — the breakdown
// hook walks the indexed key-tuples and aggregates min/max timestamps per
// product without loading flow rows. Existing rows are reindexed in place
// by Dexie on first open after upgrade.
// v33: index `whatsappKeyId` on `whatsappMessages`. Reconciliation of
// optimistic outbound messages was polling the full table every 500 ms via
// `getAll` + JS `find`; the index lets the polling loop look up the real
// row by key directly. Existing rows are reindexed in place by Dexie.
// v34: inventory's missing offline-first siblings land — `locations`, `lots`,
// `stocks` get their own Dexie stores so the AI proposal pipeline and the
// stocks UI can drive optimistic writes through the same SyncEngine as
// products/partners. `stocks` carries the compound `[companyId+productId]`
// index because every recap surface filters by product first.
// v35: `product_units` becomes a first-class offline-first resource — closes
// the last ERP DB-direct write path for inventory (M3 conversion sync now
// pushes weight updates via V2, fanning out over SSE). The store carries
// the same `[companyId+productId]` compound as stocks because recap surfaces
// scope by product when looking up the per-unit weight.
// v36: partner-account (encours, running per-partner balance) lands as an
// offline-first resource so the recap PartnerAccountBadge can render from
// Dexie instead of the legacy `useGlobalPartnerAccounts` TanStack fetch,
// and so ERP-side updates (M3 FTP encours sync) fan out via V2 SSE
// instead of stkdb-direct writes. Compound `[companyId+partnerId]` index
// because the 1:1 lookup by partnerId is the hot path.
// v37: `prices` (client-group product prices) becomes a first-class
// offline-first resource. ERP fan-out (M3 today) now writes through V2
// `/api/v2/prices/internal/changes` → SSE delivers `price.{created,updated}`
// to every connected client's Dexie copy. Compound indexes
// `[companyId+productId]` (recap row → "what's the right price for this
// row?") + `[companyId+productId+clientGroupId]` (the actual partner-group
// override resolver) keep the recap effective-price calculation O(1)
// indexed lookups instead of full-store scans.
// v38: in-app feedback (`feedbacks` + `feedbackMessages`) lands as a
// first-class offline-first resource pair so users can file errors /
// asks / issues / improvements and see the company-wide conversation
// thread without a round-trip.
// v39: add `[companyId+category+deliveryDate]` and
// `[companyId+category+orderDate]` compounds on `preOrders`. The recap
// hard-cap (`.limit(2001)`) was applied on the date-only compound *before*
// any category filter, so /purchases (category=1) could silently lose
// purchase rows behind the oldest 2000 of the combined sales+purchases set.
// The category-prefixed compounds let each mode hit its own
// oldest-N-of-category budget. Backfills `category = 0` on any row that
// lacks the field (Dexie compound indexes skip rows where any indexed
// field is missing, so a legacy sales row with `category === undefined`
// would silently disappear from /recap when the category-prefixed
// compound is used). Backend has been NOT NULL DEFAULT 0 since v30-era,
// so the backfill is purely defensive — it touches only rows written by
// older browser sessions before the field was on the wire.
// Note: the AI Inbox does NOT add an offline store — notifications ride the
// chat_session's own `unread` boolean (a non-indexed field, no schema-string
// change), so that branch left DB_VERSION at 40.
// v41 (STK-1322): adds the `_ephemeral` side table for on-demand / windowed
// eviction bookkeeping. Declared as a separate version block below so the v40
// category backfill still runs for users upgrading from <40.
// v42: typed AI suggestion sidecar resources (`preOrderSuggestions`,
// `preOrderFlowSuggestions`, `productPriceSuggestions`,
// `partnerProductPriceSuggestions`, `clientGroupProductPriceSuggestions`,
// `flowSuggestions`) replace the `ai_suggestions[]` JSON blocks + legacy
// `proposal_batch` payloads. The recap reads pending rows (filtered by
// `targetId`) and stamps `accepted_at`/`declined_at` via push-changes on resolve.
// New version (not folded into v41) so users already migrated to v41 still
// create the suggestion stores on upgrade.
// v43 (STK-2227): `emailContacts` re-keyed from `userId` to `emailAccountId`
// (mailbox scope, not sender scope). New index → version bump; see the
// detailed comment on the v43 block below for why the store is also wiped.
export const DB_VERSION = 43

export const offlineDb = new Dexie(DB_NAME)

// Surface IndexedDB blockage with an actionable message. Fires when another
// connection (most commonly a second tab of the app) holds the DB open while
// this tab tries to delete or upgrade it. Without this hook the only signal
// is a silent hanging promise, and the user thinks logout is broken.
offlineDb.on("blocked", () => {
  console.warn(
    `[offlineDb] another connection is blocking schema upgrade or delete of ` +
      `"${DB_NAME}". Close other tabs of this app to release the lock.`
  )
  // Surface the block so the boot screen can escape a hang instead of waiting
  // forever on `offlineDb.open()` (which never resolves while blocked).
  notifyDbBlocked()
})

// Once the DB actually opens, any earlier `blocked` verdict is stale — the
// connection that held us has released. Clear it (Dexie fires `ready` on every
// successful open) so a later boot attempt in this tab isn't diagnosed as
// multi-tab-blocked (with its shortened stall fuse) after the block is gone.
offlineDb.on("ready", () => {
  clearDbBlocked()
})

// Cross-tab coordination for schema upgrades. `versionchange` fires on THIS
// tab's connection when ANOTHER connection wants to upgrade (a deploy bumped
// `DB_VERSION` and a new tab opened the newer schema) or delete the DB (logout
// / reset in another tab). Registering a handler overrides Dexie's default
// close-on-versionchange, so we must close the connection ourselves in BOTH
// cases — otherwise our still-open old connection blocks their operation
// forever (the multi-tab "stuck bootstrap" users hit after a deploy).
//
//   - Upgrade (`newVersion` is a higher number): this tab runs STALE code
//     against the OLD schema. Just closing isn't enough — the engine's
//     `recoverFromUnexpectedClose` would reopen at the old version and re-block
//     the new tab (an upgrade/reopen livelock), or throw a VersionError trying
//     to open a lower version than what's on disk. Reload instead: we pick up
//     the new bundle + schema and stop being the blocker. A breadcrumb lets the
//     reloaded tab report the stale-upgrade recovery.
//   - Delete (`newVersion === null`): logout / reset elsewhere. Close only —
//     `recoverFromUnexpectedClose` (non-destructive close) or the reset flow
//     handles repopulation; a reload here would bounce a logged-out tab.
offlineDb.on("versionchange", (event) => {
  offlineDb.close()
  if (typeof window !== "undefined" && event.newVersion != null) {
    console.warn(
      `[offlineDb] another tab upgraded "${DB_NAME}" to v${event.newVersion}; ` +
        `this tab is running stale code — reloading to pick up the new schema.`
    )
    markBootRecovery("stale-upgrade")
    window.location.reload()
  }
})

/**
 * How long `openOfflineDb` waits before assuming the open is wedged. IndexedDB
 * usually fires `blocked` when another connection holds the DB, but not always
 * (a stuck upgrade, a browser that never delivers the event): without an
 * independent watchdog a hung `open()` is a silent forever-hang. Sized above a
 * healthy open (tens of ms) yet below the boot screen's normal stall fuse, so a
 * genuinely wedged open trips the shortened recovery path promptly.
 */
const OPEN_WATCHDOG_MS = 4_000

/**
 * Open the offline DB with an independent watchdog. On a healthy open this is a
 * thin wrapper over `offlineDb.open()`. If the open hasn't resolved within
 * {@link OPEN_WATCHDOG_MS} — and no `blocked` event surfaced the contention —
 * we flip the blocked signal ourselves so the boot watchdog reveals its escape
 * hatch on the shortened fuse instead of waiting out the full stall. Safe to
 * call repeatedly: a no-op once the connection is open.
 */
export async function openOfflineDb(): Promise<void> {
  if (offlineDb.isOpen()) return
  let settled = false
  const watchdog = setTimeout(() => {
    if (settled || offlineDb.isOpen()) return
    console.warn(
      `[offlineDb] open() still pending after ${OPEN_WATCHDOG_MS}ms — treating ` +
        `as blocked (another connection or a wedged upgrade).`
    )
    notifyDbBlocked()
  }, OPEN_WATCHDOG_MS)
  try {
    await offlineDb.open()
  } finally {
    settled = true
    clearTimeout(watchdog)
  }
}

offlineDb
  .version(40)
  .stores({
    [WORKFLOW_TABLE]: WORKFLOW_TABLE_SCHEMA,
    [EXECUTION_TABLE]: EXECUTION_TABLE_SCHEMA,
    [CHAT_SESSION_TABLE]: CHAT_SESSION_TABLE_SCHEMA,
    [CHAT_MESSAGE_TABLE]: CHAT_MESSAGE_TABLE_SCHEMA,
    [CHAT_EXECUTION_TABLE]: CHAT_EXECUTION_TABLE_SCHEMA,
    [CHAT_MEDIA_TABLE]: CHAT_MEDIA_TABLE_SCHEMA,
    [PROPOSAL_BATCH_TABLE]: PROPOSAL_BATCH_TABLE_SCHEMA,
    [EMAIL_TABLE]: EMAIL_TABLE_SCHEMA,
    [SMS_CHAT_TABLE]: SMS_CHAT_TABLE_SCHEMA,
    [SMS_MESSAGE_TABLE]: SMS_MESSAGE_TABLE_SCHEMA,
    [WHATSAPP_CHAT_TABLE]: WHATSAPP_CHAT_TABLE_SCHEMA,
    [WHATSAPP_MESSAGE_TABLE]: WHATSAPP_MESSAGE_TABLE_SCHEMA,
    [PRODUCT_TABLE]: PRODUCT_TABLE_SCHEMA,
    [UNIT_TABLE]: UNIT_TABLE_SCHEMA,
    [PRODUCT_UNIT_TABLE]: PRODUCT_UNIT_TABLE_SCHEMA,
    [LOCATION_TABLE]: LOCATION_TABLE_SCHEMA,
    [LOT_TABLE]: LOT_TABLE_SCHEMA,
    [STOCK_TABLE]: STOCK_TABLE_SCHEMA,
    [PARTNER_TABLE]: PARTNER_TABLE_SCHEMA,
    [PARTNER_ADDRESS_TABLE]: PARTNER_ADDRESS_TABLE_SCHEMA,
    [EMAIL_CONTACT_TABLE]: EMAIL_CONTACT_TABLE_SCHEMA,
    [PARTNER_CONTACT_TABLE]: PARTNER_CONTACT_TABLE_SCHEMA,
    [CLIENT_GROUP_TABLE]: CLIENT_GROUP_TABLE_SCHEMA,
    [PARTNER_ACCOUNT_TABLE]: PARTNER_ACCOUNT_TABLE_SCHEMA,
    [PRE_ORDER_TABLE]: PRE_ORDER_TABLE_SCHEMA,
    [PRE_ORDER_FLOW_TABLE]: PRE_ORDER_FLOW_TABLE_SCHEMA,
    [PRE_ORDER_MESSAGE_TABLE]: PRE_ORDER_MESSAGE_TABLE_SCHEMA,
    [PRE_ORDER_SMS_MESSAGE_TABLE]: PRE_ORDER_SMS_MESSAGE_TABLE_SCHEMA,
    [PRE_ORDER_EMAIL_TABLE]: PRE_ORDER_EMAIL_TABLE_SCHEMA,
    [PRICE_TABLE]: PRICE_TABLE_SCHEMA,
    [FEEDBACK_TABLE]: FEEDBACK_TABLE_SCHEMA,
    [FEEDBACK_MESSAGE_TABLE]: FEEDBACK_MESSAGE_TABLE_SCHEMA,
    _syncMeta: "id",
    _changeQueue: "id, resource, status, createdAt",
  })
  .upgrade(async (tx) => {
    // Backfill `category = 0` on any legacy preOrders row that lacks the
    // field. Dexie compound indexes (e.g. `[companyId+category+deliveryDate]`)
    // skip rows where any indexed component is undefined, so a row written
    // by an older browser session — before the V2 sync mapper started
    // forwarding `category` — would silently disappear from /recap once
    // queries shifted to the category-prefixed compound. New rows always
    // arrive with `category` set (backend column is NOT NULL DEFAULT 0); the
    // backfill is purely a one-shot rescue for stale local data.
    await tx
      .table(PRE_ORDER_TABLE)
      .toCollection()
      .modify((po) => {
        if (po.category == null) po.category = 0
      })
  })

// v41 (STK-1322): `_ephemeral` side table — LRU/pin bookkeeping for windowed
// eviction and (STK-1323) below-floor on-demand rows. `key` PK, indexed by
// `table` (sweep by store) and `lastAccessedAt` (TTL/LRU). New table → no data
// migration; the one-time quota reclaim (delete `syncId < windowFloor` on hot
// tables) runs in the engine's eviction pass on boot, where the server floor
// is available — not here (no network in a Dexie upgrade).
offlineDb.version(41).stores({
  _ephemeral: "key, table, lastAccessedAt",
})

// v42: typed AI suggestion sidecar stores — added as a delta on top of v41 so
// users already migrated to v41 (`_ephemeral`) still create these on upgrade.
// Unchanged tables carry over; only the new suggestion stores are declared.
offlineDb.version(42).stores({
  [PRE_ORDER_SUGGESTION_TABLE]: PRE_ORDER_SUGGESTION_TABLE_SCHEMA,
  [PRE_ORDER_FLOW_SUGGESTION_TABLE]: PRE_ORDER_FLOW_SUGGESTION_TABLE_SCHEMA,
  [PRODUCT_PRICE_SUGGESTION_TABLE]: PRODUCT_PRICE_SUGGESTION_TABLE_SCHEMA,
  [PARTNER_PRODUCT_PRICE_SUGGESTION_TABLE]:
    PARTNER_PRODUCT_PRICE_SUGGESTION_TABLE_SCHEMA,
  [CLIENT_GROUP_PRODUCT_PRICE_SUGGESTION_TABLE]:
    CLIENT_GROUP_PRODUCT_PRICE_SUGGESTION_TABLE_SCHEMA,
  [FLOW_SUGGESTION_TABLE]: FLOW_SUGGESTION_TABLE_SCHEMA,
})

// v43 (STK-2227): `email_contact` re-keyed from user to email account. Rows
// already cached locally carry a dead `userId` and no `emailAccountId`; the
// shared resolver treats `emailAccountId ?? null` as a workspace-wide row, so
// a stale account-scoped row would silently start behaving as company-wide.
// Clear the store AND its `_syncMeta` cursor so the SyncEngine classes it as
// cold-start and re-pulls with the new field instead of resolving the ladder
// against stale rows.
// Queued writes are dropped for the same reason: a change enqueued by a
// pre-upgrade session carries the OLD user-scoped payload with no
// `emailAccountId`. Draining it after the cold pull would push a
// workspace-wide row (`emailAccountId` absent → null) — precisely the silent
// company-wide promotion this upgrade exists to prevent — or overwrite the
// authoritative mailbox-scoped row the pull just fetched. They cannot be
// migrated: nothing in the old payload says which mailbox was meant. So they
// are retired to the terminal `discarded` status (Constitution §2.1.8) rather
// than replayed or silently swallowed; the user re-applies the toggle against
// a real mailbox.
//
// They are marked, NOT deleted. `dequeueNext` only ever selects `pending`, so a
// discarded entry can never drain — but the change stays on disk, inspectable
// and recoverable, instead of being destroyed by a schema upgrade the user
// never asked for. Deleting outright would make an unsynced write
// unrecoverable with nothing but a console line to show for it.
offlineDb
  .version(DB_VERSION)
  .stores({ [EMAIL_CONTACT_TABLE]: EMAIL_CONTACT_TABLE_SCHEMA })
  .upgrade(async (tx) => {
    await tx.table(EMAIL_CONTACT_TABLE).clear()
    await tx.table("_syncMeta").delete(EMAIL_CONTACT_TABLE)

    const stale = await tx
      .table("_changeQueue")
      .filter(
        (entry) =>
          entry?.resource === EMAIL_CONTACT_TABLE &&
          entry?.status !== "discarded"
      )
      .toArray()
    if (stale.length > 0) {
      await tx
        .table("_changeQueue")
        .bulkPut(stale.map((entry) => ({ ...entry, status: "discarded" })))
      console.warn(
        `[offlineDb] v${DB_VERSION} (STK-2227): discarded ${stale.length} unsynced ` +
          `${EMAIL_CONTACT_TABLE} change(s) queued before the mailbox re-key — ` +
          `their user-scoped payload names no mailbox and cannot be migrated. ` +
          `They are retained in _changeQueue with status="discarded" (never ` +
          `drained) for recovery. Re-apply those AI-analysis toggles.`
      )
    }
  })
